import React, {useEffect, useMemo, useRef} from 'react';
import {BiCart, BiMinus, BiPlus} from 'react-icons/bi';
import {Button, Form, InputGroup} from 'react-bootstrap';
import {useAppDispatch, useAppSelector} from '../../../../../hooks/useTypedSelector';
import {addToCartMultiple, patchCartItem} from '../../../infrastructure/slices/cartSlice';
import {toast} from 'react-toastify';
import {useTranslation} from "react-i18next";
import {OrderItemFormatted} from "../../../domain/models/cartApiResponseModel";

type DescribableFunction = {
    (event: React.MouseEvent): void;
};

const CartButton = (props: {variant: string, handleAddToCart: DescribableFunction, disabled?: boolean}) => {
    const {variant, handleAddToCart, disabled = false} = props;

    return <Button className={'btn-cart'} variant={'success'} onClick={handleAddToCart} disabled={disabled}>
        {(!['card', 'cart'].includes(variant)) && <BiPlus size={'25px'} style={{marginRight: '8px'}} />}
        <BiCart size={'25px'} />
    </Button>
}

export function AddToCart(props: { variationId: number, quantity?: number, showBtn?: boolean, variant?: 'card' | 'product' | 'cart', disabled?: boolean, btnDisabled?: boolean, updateQuantity?: (q: number) => void, minQuantity?: number, maxQuantity?: number, onPriceRequest?: () => void }) {
    const {variationId, quantity: initialQuantity = 1, showBtn = true, variant = 'card', disabled = false, btnDisabled = false, updateQuantity = null, minQuantity = 1, maxQuantity = undefined, onPriceRequest = null} = props;
    const {t} = useTranslation('common');
    const inputRef = useRef(null);
    const dispatch = useAppDispatch();
    const {cart} = useAppSelector((state) => state.cart);

    const cartItem = cart && 'items' in cart && cart.items.find(item => item.variation_id === variationId) as OrderItemFormatted;
    const inCart = Boolean(cartItem);
    const autoUpdate = variant === 'cart';
    const background = variant === 'card' ? 'dark' : 'secondary';

    const quantity = useMemo(() => inCart && cartItem && autoUpdate ? parseInt(`${cartItem?.quantity ?? 1}`) : initialQuantity, [cartItem, initialQuantity]);
    const normalizedMaxQuantity = typeof maxQuantity === 'number' && Number.isFinite(maxQuantity) && maxQuantity >= 0 ? Math.floor(maxQuantity) : undefined;
    const effectiveMaxQuantity = normalizedMaxQuantity;
    const outOfStockForSelection = !autoUpdate && effectiveMaxQuantity !== undefined && effectiveMaxQuantity <= 0;
    const controlsDisabled = disabled || outOfStockForSelection;
    const addButtonDisabled = btnDisabled || outOfStockForSelection;

    useEffect(() => {
        const input = inputRef.current as unknown as HTMLInputElement;
        if (!input) return;
        const current = parseInt(input.value);
        let nextQuantity = outOfStockForSelection ? 0 : quantity;
        if (effectiveMaxQuantity !== undefined) {
            nextQuantity = Math.min(nextQuantity, effectiveMaxQuantity);
        }
        if (!outOfStockForSelection) {
            nextQuantity = Math.max(1, nextQuantity);
        }
        if (current !== nextQuantity) {
            input.value = `${nextQuantity}`;
        }
    }, [quantity, effectiveMaxQuantity, outOfStockForSelection]);

    const handleIncrement = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (autoUpdate) {
            if (effectiveMaxQuantity !== undefined && quantity >= effectiveMaxQuantity) {
                return;
            }
            await dispatch(addToCartMultiple({map: [{quantity: 1, id: variationId}]}));
        }
        else {
            const input = inputRef.current as unknown as HTMLInputElement;
            if (!input) return;
            const current = parseInt(input.value);
            const nextQuantity = effectiveMaxQuantity !== undefined
                ? Math.min(current + 1, effectiveMaxQuantity)
                : current + 1;
            input.value = `${nextQuantity}`;
            if (updateQuantity) {
              updateQuantity(nextQuantity);
            }
        }
    };

    const handleDecrement = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const input = inputRef.current as unknown as HTMLInputElement;
        if (!input) return;
        const current = parseInt(input.value);
        if (quantity > 1 && autoUpdate) {
            await dispatch(addToCartMultiple({map: [{id: variationId, quantity: -1}]}));
        }
        else if (current > 1) {
            input.value = `${current - 1}`;
            if (updateQuantity) {
              updateQuantity(current - 1);
            }
        }
    };

    const handleQuantityChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newQuantity = parseInt(e.target.value, 10);
        let validQuantity = isNaN(newQuantity) ? 1 : newQuantity;
        validQuantity = Math.max(outOfStockForSelection ? 0 : 1, validQuantity);
        if (effectiveMaxQuantity !== undefined) {
            validQuantity = Math.min(validQuantity, effectiveMaxQuantity);
        }
        e.target.value = `${validQuantity}`;

        if (updateQuantity) {
          updateQuantity(validQuantity);
        }
        if (cartItem && cartItem?.order?.item && autoUpdate && quantity > 0) {
            await dispatch(patchCartItem({
                itemId: cartItem.order.item as unknown as number,
                cartId: cartItem.order.id as unknown as number,
                quantity: validQuantity
            }));
        }
    };

    const handleAddToCart = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (outOfStockForSelection) {
            return;
        }
        if (variationId) {
            const input = inputRef.current as unknown as HTMLInputElement;
            if (!input) return;
            const current = parseInt(input.value);
            const rawAmount = autoUpdate ? quantity : current;
            const amt = effectiveMaxQuantity !== undefined
                ? Math.min(rawAmount, effectiveMaxQuantity)
                : rawAmount;
            if (amt > 0) {
                if (minQuantity > amt) {
                  if (onPriceRequest) {
                    onPriceRequest();
                  }
                }
                else {
                  dispatch(addToCartMultiple({map: [{quantity: amt, id: variationId}]})).then((result) => {
                      if (addToCartMultiple.fulfilled.match(result)) {
                          const addedCartItem = result.payload?.find(item => item.id === variationId);
                          const added = addedCartItem?.added ?? amt;
                          if (!autoUpdate) {
                              const localInput = inputRef.current as unknown as HTMLInputElement;
                              if (localInput) {
                                  localInput.value = '1';
                              }
                              if (updateQuantity) {
                                  updateQuantity(1);
                              }
                          }
                          if (added <= 0) {
                              toast.error(t('No products were added because there is no stock available'));
                          }
                          else if (added < rawAmount) {
                              toast.warning(t('Only @quantity product(s) were added because of limited stock').replace('@quantity', `${added}`));
                          }
                          else if (addedCartItem?.action_type === 'tht') {
                              toast.success(t('Added to you cart with THT reservation').replace('%sku', addedCartItem.sku));
                          }
                          else {
                              toast.success(t('Added to you cart'));
                          }
                      }
                  });
                }
            }
        }
    };

    return (
        <>
            <InputGroup className={'quantity-input-group flex-grow'}>
                <Button variant={background} className={'decrement'} onClick={handleDecrement} type={'button'} disabled={controlsDisabled}>
                    <BiMinus size={'20px'} />
                </Button>
                <Form.Control className={'bg-' + background + ' cart-qty flex-grow'} defaultValue={quantity} onChange={handleQuantityChange}
                              ref={inputRef} readOnly={controlsDisabled} min={outOfStockForSelection ? 0 : 1} max={effectiveMaxQuantity} />
                <Button variant={background} className={'increment'} onClick={handleIncrement} type={'button'} disabled={controlsDisabled}>
                    <BiPlus size={'20px'} />
                </Button>
            </InputGroup>
            {showBtn && <CartButton variant={variant} handleAddToCart={handleAddToCart} disabled={addButtonDisabled} />}
        </>
    );
}
